import { useState } from 'react';
import { history } from 'umi';
import useStoreFilterExtend from '@/pages/intelligence/hooks/useStoreRankFilterExtend';
import moment from 'moment';
import type { IStoreRankTopFilter, IStoreRankTop, IStoreRankTopRank } from '@/pages/intelligence/types/storeRank';
import { fetchGameGenreProfileMetricsNoType } from '@intelligence/services/common';
import { onFilterConfirm as onSetFilter } from '@intelligence/components/Filter/util';
import { formatMiniGameParams } from '@/pages/intelligence/_config/storeRank/utils';
import { GAME_KEY } from '@/pages/intelligence/_config/storeRank/common';

export default function useMiniGameRankings(props: {
  // 榜单卡片点击标题 跳转榜单下钻页的 路由path
  morePagePath: string;
  // 榜单卡片点击游戏 跳转游戏详情页的 路由path
  detailPagePath?: string;
  // 平台key映射，用于处理接口入参时，平台值的特殊映射
  platformKeysMapping?: {
    [key: string]: string;
  };
  // 页面打点配置的key
  pageLogCfgKey: string;
  // 是否操作过筛选器
  isOperate: boolean;
  // 初始化筛选器数据
  initialFilter: IStoreRankTopFilter;
  metricTypeList?: string[];
}) {
  const {
    // 榜单卡片点击标题 跳转榜单下钻页的 路由path
    morePagePath,
    // 榜单卡片点击游戏 跳转游戏详情页的 路由path
    detailPagePath,
    // 初始化筛选器数据
    initialFilter,
    metricTypeList,
  } = props;
  // console.debug('useMiniGameRankings', props)
  const { setFilterExtend: setStoreFilterExtend } = useStoreFilterExtend({});

  const [filter, setFilter] = useState<IStoreRankTopFilter>(initialFilter);

  const [isLoading, setIsLoading] = useState(true);

  const [tops, setTops] = useState<IStoreRankTop[]>([]);

  const updateTops = async (payload: IStoreRankTopFilter) => {
    // console.debug('updateTops', payload)
    try {
      setIsLoading(true);
      const fetchParamsValue = {
        ...formatMiniGameParams(payload),
        order_by: ['game_rank asc'],
        group_by: ['game_id', 'minigame_type'],
        minigame_types: metricTypeList,
        page: 1,
        page_size: 100,
      } as any;

      const res = await fetchGameGenreProfileMetricsNoType(fetchParamsValue);

      const { granularity, start_date: startDate, end_date: endDate } = res?.req ?? {};
      const topListTemp = res?.data?.list ?? [];

      // 使用传入的 payload 而不是当前 filter 状态，确保 URL filter_tag_name 解析的值能被保留
      const filterTmp: IStoreRankTopFilter = {
        ...payload,
        granularity: granularity ?? payload?.granularity,
        start_date: startDate ?? payload?.start_date ?? '',
        end_date: endDate ?? payload?.end_date ?? '',
      };
      setFilter(filterTmp);
      let dateType = 'date';
      switch (granularity) {
        case 'daily':
          dateType = 'date';
          break;
        case 'weekly':
          dateType = 'week';
          break;
        case 'monthly':
          dateType = 'month';
          break;
        default:
          dateType = 'date';
          break;
      }
      const beforeFilterExtend = {
        rangeTime: {
          type: dateType,
          value: [moment(startDate), moment(endDate)],
        },
      };

      const { apiValue } = onSetFilter(beforeFilterExtend);
      setStoreFilterExtend(apiValue);
      setTops(topListTemp);
    } catch (exception) {
      setTops([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterConfirm = (payload: Partial<IStoreRankTopFilter>) => {
    const filterTmp: IStoreRankTopFilter = {
      ...filter,
      ...payload,
    };
    setFilter(filterTmp);
    updateTops(filterTmp);
  };

  const handleFilterReset = () => { };

  // 构建榜单来源筛选参数
  const buildSearch = (source: string) => {
    const { granularity, start_date: startDate, end_date: endDate } = filter;
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.append('source', encodeURIComponent(source));
    urlSearchParams.append('granularity', encodeURIComponent(granularity ?? ''));
    urlSearchParams.append('start_date', encodeURIComponent(startDate ?? ''));
    urlSearchParams.append('end_date', encodeURIComponent(endDate ?? ''));
    return urlSearchParams.toString();
  };

  const handleGoToStoreRankMore = (payload: IStoreRankTop) => {
    if (!morePagePath) return;
    history.push({
      pathname: morePagePath,
      search: buildSearch(payload?.name ?? ''),
      state: { noMatch: true }, // 保持导航栏高亮在上一级菜单
    });
  };

  // 获取游戏详情页路由 path
  // 注：微小游戏 game_id 不满足 unified_id (e/u) 格式校验，跳转以 gameId 非空为准
  const getDetailGamePath = (
    topPayload: IStoreRankTop,
    topRankPayload: IStoreRankTopRank,
    _isValidGameId: boolean,
  ) => {
    if (!detailPagePath) return '';
    const gameId = (topRankPayload as any)?.[GAME_KEY];
    if (!gameId) return '';
    return `${detailPagePath}/${gameId}`;
  };

  const handleCardGameClick = (
    topPayload: IStoreRankTop,
    topRankPayload: IStoreRankTopRank,
    isValidGameId: boolean,
  ) => {
    const path = getDetailGamePath(topPayload, topRankPayload, isValidGameId);
    if (path) history.push(path);
  };

  return {
    // 榜单数据加载状态
    isLoading,
    // 榜单数据
    tops,
    // 筛选器数据
    filter,
    // 页面初始化时，需要自行调用该方法，查询数据，因为有的页面 不用等筛选器数据接口返回 校验 继承的筛选器值，有的页面 又需要校验，所以通用hooks内 不做这个业务判断，再页面自行决定 什么时候初始化查询数据
    onPageInit: updateTops,
    // 筛选器confirm的回调
    onFilterConfirm: handleFilterConfirm,
    // 筛选器reset的回调
    onFilterReset: handleFilterReset,
    // 榜单卡片点击游戏的回调（跳转游戏详情页）
    onCardGameClick: handleCardGameClick,
    // 榜单卡片点击标题的回调
    onGoToStoreRankMore: handleGoToStoreRankMore,
    // 获取游戏详情页路由 path
    getDetailGamePath,
  };
}
